import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import useOTPLogin from 'hooks/useOTPLogin';

export default function EmailOTPSignin() {
  const { t } = useTranslation();

  const {
    isOtpSent,
    session,
    isLoading,
    mutate,
    setIsLoading,
    resendOTP,
    isResending,
    isNewUser,
    mutateSignUp,
    resendSignupOtp,
  } = useOTPLogin('email');

  const validator = z.object({
    email: z
      .string()
      .email({
        message: 'Please enter a valid email address.',
      })
      .min(1, {
        message: 'Please enter email.',
      }),
    otp: isOtpSent
      ? z.string().min(1, { message: 'Please provide OTP' })
      : z.string().optional(),
  });

  const form = useForm<z.infer<typeof validator>>({
    resolver: zodResolver(validator),
    defaultValues: {
      email: '',
      otp: '',
    },
  });

  const onSubmit = (values: z.infer<typeof validator>) => {
    setIsLoading(true);
    if (isNewUser) {
      return mutateSignUp({
        otp_value: values.otp!,
        username: values.email.toLowerCase(),
        session,
      });
    }

    if (isOtpSent) {
      mutate({
        username: values.email.toLowerCase(),
        auth_type: 'verify_auth_otp',
        session,
        otp_value: values.otp,
      });
    } else {
      mutate({ username: values.email.toLowerCase(), auth_type: 'otp' });
    }
  };

  return (
    <div className='space-y-8'>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='w-full space-y-8'
        >
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor='email'>{t('email')}</FormLabel>
                <FormControl>
                  <Input {...field} id='email' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isOtpSent && (
            <div>
              <FormField
                control={form.control}
                name='otp'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('otp')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => {
                          const { value } = e.target;
                          const numericRegex = /^\d{0,6}$/;
                          if (numericRegex.test(value)) {
                            field.onChange(value);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className='flex justify-end'>
                <Button
                  type='button'
                  variant='link'
                  disabled={isLoading || isResending}
                  onClick={() => {
                    if (isNewUser) {
                      resendSignupOtp(form.getValues('email'));
                    } else {
                      resendOTP(form.getValues('email'));
                    }
                  }}
                >
                  {isResending && (
                    <Loader2
                      role='spinbutton'
                      className='mr-2 size-4 animate-spin'
                    />
                  )}
                  {t('resent_otp')}
                </Button>
              </div>
            </div>
          )}

          <div className='flex flex-col justify-center'>
            <Button
              type='submit'
              className='px-6 font-semibold rounded-full'
              disabled={!form.formState.isValid || isLoading || isResending}
            >
              {isLoading && (
                <Loader2
                  role='progressbar'
                  className='mr-2 size-4 animate-spin'
                />
              )}
              {isOtpSent ? t('verify_otp') : t('get_otp')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
